import React, { useContext, useEffect, useMemo, useState } from 'react';

import {
    ContractDeals,
    GetContractDealsByIdsInput
} from '@amzn/coral_com-amazon-musicpapercontractconfigurationservice';
import { UnknownCoralError } from '@amzn/ihm-js-coral-client';
import Box from '@amzn/meridian/box';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';

import { Switch, useRouteMatch } from 'react-router-dom';
import { contractConfigurationClient } from 'src/api/client';
import { ProtectedRoute } from 'src/components/ProtectedRoute';
import { Status } from 'src/constants';
import { CAM_ROLE_SET, EDITOR_ROLE_SET, VIEWER_ROLE_SET } from 'src/constants/role';
import { ROUTES, URL_PARAMS } from 'src/constants/routes';
import { AppContext } from 'src/contexts/appContext';
import { IVendorMapEntry, IVendorRowData } from 'src/types/IVendor';
import { getGeneralStatus } from 'src/util/grid/status';

import { AddVendor } from '../AddVendor';
import { BaseAgGrid } from '../BaseAgGrid';
import { ButtonGroupForGrid } from '../ButtonGroupForGrid';
import { FilterInput } from '../FilterInput';
import { VendorDetails } from '../VendorDetails';
import './Vendors.scss';
import { vendorCodesColDefs, vendorCodesColSortModel } from './vendorsGridColDefs';

export const Vendors: React.FC = () => {
    const [isDataLoaded, setIsDataLoaded] = useState(false);
    const [filterText, setFilterText] = useState('');
    const [deals, setDeals] = useState<ContractDeals>([]);
    const { path } = useRouteMatch();

    const { setTopAlertMsg, entities } = useContext(AppContext);

    useEffect(() => {
        contractConfigurationClient
            .getContractDealsByIds(
                GetContractDealsByIdsInput.clone({
                    entityTypeIds: [1],
                    includeIneligibleContractDeals: true
                })
            )
            .toPromise()
            .then((response) => {
                setIsDataLoaded(true);
                setDeals(response.contractDeals);
            })
            .catch((error: UnknownCoralError) => {
                // eslint-disable-next-line no-console
                console.error(
                    'getEntitiesByIds/getContractDealsByIds unexpected error',
                    error.receivedException.message
                );
                setTopAlertMsg(error.receivedException.message);
            });
    }, []);

    const vendorsRowData: IVendorRowData[] = useMemo(() => {
        const vendorMap: Record<string, IVendorMapEntry> = {};
        const vendors = [] as IVendorRowData[];

        entities?.forEach((entity) => {
            vendorMap[entity.entityId!] = {
                entityId: entity.entityId!,
                entityName: entity.entityName ?? '',
                entityType: entity.entityType?.entityTypeName ?? '',
                catalogCodeSet: new Set<string>(entity.entityVendorCodes?.map((evc) => evc.catalogVendorCode!) ?? []),
                royaltyCodeSet: new Set<string>(entity.entityVendorCodes?.map((evc) => evc.royaltyVendorCode!) ?? []),
                activeDealServiceSet: new Set<string>()
            };
        });

        deals.forEach((contractDeal) => {
            contractDeal.entityActiveIntervals.forEach((entityActiveInterval) => {
                if (
                    vendorMap[entityActiveInterval.entityId] &&
                    getGeneralStatus(entityActiveInterval) === Status.Active
                ) {
                    vendorMap[entityActiveInterval.entityId].activeDealServiceSet.add(contractDeal.serviceName);
                }
            });
        });
        Object.keys(vendorMap).forEach((entityId) => {
            vendors.push({
                ...vendorMap[entityId],
                catalogVendorCode: Array.from(vendorMap[entityId].catalogCodeSet).sort().join(', '),
                royaltyVendorCode: Array.from(vendorMap[entityId].royaltyCodeSet).sort().join(', '),
                dealStatus: Array.from(vendorMap[entityId].activeDealServiceSet)
                    .sort((a, b) => b.localeCompare(a))
                    .join(', ')
            });
        });

        return vendors;
    }, [entities, deals]);

    const vendorsContent = (
        <Column className="vendors-div" width="100%" height="100%" minHeight={480} spacing="300">
            <Box type="fill" spacingInset="400">
                <Row alignmentHorizontal="justify">
                    <FilterInput filterText={filterText} setFilterText={setFilterText} />
                    <ButtonGroupForGrid showAddVendor={true} />
                </Row>
            </Box>
            <div className="vendors-grid">
                <BaseAgGrid
                    columnDefs={vendorCodesColDefs}
                    sortModel={vendorCodesColSortModel}
                    rowData={vendorsRowData}
                    filterText={filterText}
                    loading={!isDataLoaded}
                />
            </div>
        </Column>
    );

    return (
        <div className="vendors-container">
            <Switch>
                <ProtectedRoute exact requiredRoleSet={VIEWER_ROLE_SET} path={path}>
                    {vendorsContent}
                </ProtectedRoute>
                <ProtectedRoute requiredRoleSet={EDITOR_ROLE_SET} path={`${path}${ROUTES.VENDORS.ADD}`}>
                    <AddVendor />
                </ProtectedRoute>
                <ProtectedRoute
                    requiredRoleSet={EDITOR_ROLE_SET}
                    path={`${path}${ROUTES.VENDORS.EDIT}${URL_PARAMS.VENDOR_ID}`}
                >
                    <AddVendor />
                </ProtectedRoute>
                <ProtectedRoute requiredRoleSet={VIEWER_ROLE_SET} path={`${path}${URL_PARAMS.VENDOR_ID}`}>
                    <VendorDetails />
                </ProtectedRoute>
            </Switch>
        </div>
    );
};
